import React, { Component, Children } from 'react';
import { injectIntl } from 'react-intl';
import { PropTypes } from 'prop-types';
import classnames from 'classnames';
import times from 'lodash/times';
import isArray from 'lodash/isArray';
import memoize from 'lodash/memoize';

import theme from 'src/styles/components/Carousel.module.scss';

import IconButton from 'src/components/IconButton';
import CarouselIndicators from 'src/components/Carousel/CarouselIndicators';
import Loader from 'src/components/Loader';

const getElementStyle = memoize((elementWidth) => {
    if (!elementWidth) {
        return null;
    }

    return {
        minWidth: elementWidth,
        width: elementWidth
    };
});

export const innerDimensions = (node) => {
    const computedStyle = getComputedStyle(node);

    let width = node.clientWidth; // width with padding
    let height = node.clientHeight; // height with padding

    height -= parseFloat(computedStyle.paddingTop) + parseFloat(computedStyle.paddingBottom);
    width -= parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight);
    return { height, width };
};

class Carousel extends Component {
    dist = 0;

    startX = 0;

    startY = 0;

    startTime = 0;

    threshold = 50;

    allowedTime = 300;

    elapsedTime = 0;

    shouldSwipe = false;

    isMoveActive = false;

    mouseSwipePercentThreshold = 30;

    percentageScrollWidth = 100;

    itemsPerScrollContainer = 1;

    itemsOffset = 1;

    containerWidth = 0;

    isPrevSlideAvailable = false;

    isNextSlideAvailable = false;

    areItemsGratherThanViewport = false;

    elementWidth = 0;

    mexTranslate = 0;

    constructor(props) {
        super(props);

        this.state = {
            currentSlide: 0,
            isInitialized: false
        };
    }

    componentDidMount() {
        const { isAutoSlideEnabled, children } = this.props;

        if (isAutoSlideEnabled) {
            this.resetTimer();
        }

        if (((!isArray(children) && children) || (isArray(children) && children.length))
            && !this.state.isInitialized) {
            this.initialize();
        } else if (this.props.isInitialized) {
            this.initialize();
        }
    }

    componentDidUpdate({ isInitialized, children, currentSlide }, prevState) {
        const arePropInitializedStateChange = this.props.isInitialized && this.props.isInitialized !== isInitialized;

        const areChildrenAvailable = (!isArray(this.props.children) && this.props.children)
            || (isArray(this.props.children) && Children.count(this.props.children));

        const areChildrenLengthChanged = Children.count(this.props.children) !== Children.count(children);

        if ((
            (areChildrenAvailable && !this.state.isInitialized) || arePropInitializedStateChange)
            && !areChildrenLengthChanged) {
            this.initialize();
        }

        if (areChildrenLengthChanged) {
            this.prepareContainer(true).then(() => {
                if (!this.isUnmounting && this.maxFiniteSlide < this.state.currentSlide) {
                    this.slideTo(this.maxFiniteSlide);
                }
            });
        }

        const hasCurrentSlideChanged = this.state.currentSlide !== prevState.currentSlide;

        if (hasCurrentSlideChanged && this.props.isAutoSlideEnabled) {
            this.resetTimer();
        }

        if (this.props.currentSlide !== currentSlide && this.props.currentSlide !== this.state.currentSlide) {
            this.slideTo(this.props.currentSlide);
        }
    }

    componentWillUnmount() {
        this.isUnmounting = true;

        if (this.props.isAutoSlideEnabled) {
            window.clearInterval(this.intervalId);
        }

        clearTimeout(this.initializeTimeout);

        window.removeEventListener('resize', this.prepareContainer);

        document.body.removeEventListener('touchmove', this.handleDocumentMove);
    }

    getSlideNextTime = () => {
        const { children, slideNextTime } = this.props;

        if (children.length === 0) {
            return slideNextTime;
        }

        const normalizedChildren = Children.toArray(children);
        const slide = normalizedChildren[this.state.currentSlide % normalizedChildren.length];

        if (slide) {
            return slide.props.slideNextTime || slideNextTime;
        }

        return slideNextTime;
    }

    getTranslateValue(translation) {
        if (translation === 0) {
            return translation;
        }

        return this.maxTranslate < translation
            ? this.maxTranslate
            : translation;
    }

    initialize = () => {
        this.initializeTimeout = setTimeout(() => {
            this.prepareContainer(true).then(() => {
                if (!this.state.isInitialized && !this.isUnmounting) {
                    this.setState({ isInitialized: true });
                }
            });
        }, 100);

        window.addEventListener('resize', this.prepareContainer);
    }

    disableTimer = () => {
        if (this.intervalId) {
            window.clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    resetTimer = () => {
        if (this.intervalId) {
            window.clearInterval(this.intervalId);
        }

        this.intervalId = window.setInterval(this.slideNext, this.getSlideNextTime());
    }

    slideNext = (isSwipeLeft) => {
        this.slideBy(this.itemsPerScrollContainer, isSwipeLeft);
    }

    slideViewport = (isSwipeLeft) => {
        const items = !isSwipeLeft
            ? Array.from(this.scrollContainer.children).slice(this.state.currentSlide).reduce((result, item) => {
                if (result.width + item.clientWidth - (item.clientWidth * (this.mouseSwipePercentThreshold / 100)) <= this.containerWidth) {
                    return {
                        width: result.width + item.clientWidth,
                        number: result.number + 1
                    };
                }

                return result;
            }, { width: 0, number: 0 })
            : Array.from(this.scrollContainer.children).slice(0, this.state.currentSlide).reverse().reduce((result, item) => {
                // eslint-disable-next-line max-len
                if (result.width + item.clientWidth - (item.clientWidth * ((100 - this.mouseSwipePercentThreshold) / 100)) <= this.containerWidth) {
                    return {
                        width: result.width + item.clientWidth,
                        number: result.number + 1
                    };
                }

                return { ...result, width: result.width + item.clientWidth };
            }, { width: 0, number: 0 });

        this.slideBy(items.number, isSwipeLeft);
    }

    slideBy = (itemsCount, isSwipeLeft) => {
        const { children, transitionDuration, isInfinite } = this.props;

        const direction = isSwipeLeft ? -1 : 1;

        this.setState(({ currentSlide }) => ({ currentSlide: currentSlide + (itemsCount * direction) }), () => {
            this.scrollContainer.classList.remove(theme.noTransition);

            const { currentSlide } = this.state;

            if (!isInfinite) {
                let expectedCurrentSlide = currentSlide;
                if (currentSlide >= this.maxFiniteSlide) {
                    expectedCurrentSlide = this.maxFiniteSlide;
                    this.isNextSlideAvailable = false;
                    this.isPrevSlideAvailable = this.areItemsGratherThanViewport;
                } else if (currentSlide <= 0) {
                    expectedCurrentSlide = 0;
                    this.isPrevSlideAvailable = false;
                    this.isNextSlideAvailable = this.areItemsGratherThanViewport;
                } else {
                    this.isPrevSlideAvailable = this.areItemsGratherThanViewport;
                    this.isNextSlideAvailable = this.areItemsGratherThanViewport;
                }

                this.setState({ currentSlide: expectedCurrentSlide }, () => {
                    this.scrollContainer.classList.remove(theme.noTransition);
                    this.transformContainer();
                });
            } else if (currentSlide >= Children.count(children) || currentSlide < 0) {
                const newCurrentSlideValue = currentSlide >= Children.count(children)
                    ? currentSlide - Children.count(children)
                    : Children.count(children) + currentSlide;
                this.transformContainer();

                this.setState({ currentSlide: newCurrentSlideValue }, () => {
                    setTimeout(() => {
                        this.scrollContainer.classList.add(theme.noTransition);
                        this.transformContainer();
                    }, transitionDuration);
                });
            } else {
                this.transformContainer();
            }
        });
    }

    slideTo = (slide) => {
        this.scrollContainer.classList.remove(theme.noTransition);

        if (slide) {
            this.setState({ currentSlide: slide }, () => {
                this.transformContainer();
            });
        } else {
            this.setState({ currentSlide: 0 }, () => {
                this.transformContainer();
            });
        }

        if (this.props.isAutoSlideEnabled) {
            this.resetTimer();
        }
    }

    transformContainer = () => {
        if (!this.props.shouldSlideByStaticWidth) {
            const transformX = Array.from(this.scrollContainer.children)
                .slice(0, this.state.currentSlide)
                .reduce((result, item) => result + item.clientWidth, 0);

            this.scrollContainer.style.transform = `translateX(-${this.getTranslateValue(transformX)}px)`;
        } else {
            const offset = this.itemsOffset * this.percentageScrollWidth;
            const transform = (this.state.currentSlide * this.percentageScrollWidth) + offset;

            this.scrollContainer.style.transform = `translateX(-${transform}%)`;
        }
    }

    handleMouseDown = (e) => {
        this.shouldSwipe = false;

        e.stopPropagation();

        if (!this.isScrollAvailable) {
            return null;
        }

        this.isMoveActive = true;
        this.scrollContainer.classList.add(theme.moveActive);

        this.prepareSwipeParameters(e);
    }

    handleMouseUp = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!this.isScrollAvailable) {
            return null;
        }

        this.processSwipe(e);
    }

    handleMouseLeave = (e) => {
        if (!this.isScrollAvailable) {
            return null;
        }

        if (this.isMoveActive) {
            this.processSwipe(e);
        }
    }

    handleMouseMove = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!this.isScrollAvailable) {
            return null;
        }

        if (this.isMoveActive) {
            this.moveCarousel(e);
        }
    }

    handleTouchStart = (e) => {
        this.shouldSwipe = false;

        if (e.cancelable) {
            e.preventDefault();
        }

        if (!this.isScrollAvailable) {
            return null;
        }

        const touchobj = e.changedTouches[0];

        this.prepareSwipeParameters(touchobj);
    }

    handleTouchMove = (e) => {
        const touchobj = e.changedTouches[0];

        if (!this.isScrollAvailable) {
            return null;
        }

        this.moveCarousel(touchobj);

        e.stopPropagation();
    }

    handleTouchEnd = (e) => {
        if (e.cancelable && (this.isMoveActive || this.shouldSwipe)) {
            e.preventDefault();
        }

        e.stopPropagation();

        if (!this.isScrollAvailable) {
            return null;
        }

        const touchobj = e.changedTouches[0];

        this.processSwipe(touchobj);
    }

    // eslint-disable-next-line react/sort-comp
    handleIndicatorClick = (indicator) => { this.slideTo(indicator * this.itemsPerScrollContainer); }

    elementClickEvent = (e) => {
        if (this.isMoveActive || this.shouldSwipe) {
            if (e && e.preventDefault) {
                e.preventDefault();
            }

            return false;
        }

        return true;
    }

    prepareContainer = async (isNotInitialized) => {
        let doit;
        clearTimeout(doit);
        doit = setTimeout(await this.prepareContainerParameters(isNotInitialized), 100);

        return new Promise((resolve) => {
            if (this.scrollContainer) {
                if (isNotInitialized) {
                    this.scrollContainer.classList.add(theme.noTransition);
                }

                this.transformContainer();
            }
            resolve();
        });
    }

    prepareContainerParameters(isNotInitialized) {
        const { elementWidth, slideItemsCount, children, isInfinite, shouldSlideByStaticWidth } = this.props;

        const itemsCount = isArray(children) ? Children.count(children) : 0;

        return new Promise((resolve) => {
            if ((slideItemsCount || elementWidth || !shouldSlideByStaticWidth) && this.scrollContainer) {
                this.containerWidth = this.scrollContainer.getBoundingClientRect().width;
                const containerInnerWidth = innerDimensions(this.scrollContainer, null).width;

                this.elementWidth = containerInnerWidth < elementWidth ? containerInnerWidth : elementWidth;
                this.itemsPerScrollContainer = slideItemsCount || (Math.floor(this.containerWidth / this.elementWidth) || 1);

                this.maxTranslate = (this.scrollContainer.scrollWidth - this.containerWidth);

                const itemsOffsetCount = this.itemsPerScrollContainer;
                const areItemsOffsetCountGratherThanChildren = itemsOffsetCount >= itemsCount;

                if (!areItemsOffsetCountGratherThanChildren && isInfinite) {
                    this.itemsOffset = (itemsOffsetCount + 1) * 2;
                } else {
                    this.itemsOffset = 0;
                }

                this.maxFiniteSlide = itemsCount - this.itemsPerScrollContainer;

                if (slideItemsCount && itemsCount > itemsOffsetCount) {
                    this.percentageScrollWidth = 100 / slideItemsCount;
                } else if (this.elementWidth && itemsCount > itemsOffsetCount) {
                    this.percentageScrollWidth = (this.elementWidth / this.containerWidth) * 100;
                } else if (!shouldSlideByStaticWidth) {
                    this.valueScrollWidth = 100;
                } else {
                    this.percentageScrollWidth = 0;
                }

                this.areItemsGratherThanViewport = slideItemsCount
                    ? slideItemsCount < itemsCount
                    : (this.containerWidth < itemsCount * this.elementWidth
                        || this.containerWidth < Array
                            .from(this.scrollContainer.children)
                            .reduce((result, item) => result + item.offsetWidth, 0));

                this.isScrollAvailable = (Boolean(this.itemsOffset) || !isInfinite)
                    && this.areItemsGratherThanViewport;

                if (isInfinite) {
                    this.isPrevSlideAvailable = true;
                    this.isNextSlideAvailable = true;
                } else {
                    this.isPrevSlideAvailable = false;
                    this.isNextSlideAvailable = this.areItemsGratherThanViewport;
                }

                const areItemsOffsetCountGratherThanItemsOffset = itemsOffsetCount > this.itemsOffset;
                if (areItemsOffsetCountGratherThanItemsOffset && !this.state.currentSlide) {
                    this.setState({
                        currentSlide: this.itemsOffset
                    });
                }

                if (!isNotInitialized && !this.isUnmounting && !this.state.currentSlide) {
                    this.setState({
                        currentSlide: itemsOffsetCount
                    }, resolve);
                } else {
                    resolve();
                }

                const indicatorsCount = Math.ceil(Children.count(children) / this.itemsPerScrollContainer);
                if (this.state.indicatorsCount !== indicatorsCount && !this.isUnmounting) {
                    this.setState({
                        indicatorsCount
                    });
                }

                this.transformContainer();
            } else {
                resolve();
            }
        });
    }

    prepareSwipeParameters(target) {
        this.disableTimer();

        this.shouldPreventTouch = false;
        this.shouldSwipe = false;
        this.dist = 0;
        this.startX = target.pageX;
        this.startY = target.pageY;
        this.startTime = new Date().getTime();

        this.scrollContainer.classList.add(theme.noTransition);
    }

    moveCarousel(target) {
        const dist = this.startX - target.pageX;
        const percentMove = (dist / this.scrollContainer.clientWidth) * 100;

        if (dist) {
            this.isMoveActive = true;
            this.scrollContainer.classList.add(theme.moveActive);
        }

        if (!this.props.shouldSlideByStaticWidth) {
            const transformX = Array.from(this.scrollContainer.children)
                .slice(0, this.state.currentSlide)
                .reduce((res, item) => res + item.clientWidth, 0);

            const items = dist > 0
                ? Array.from(this.scrollContainer.children).slice(this.state.currentSlide).reduce((result, item) => {
                    if (result.width + item.clientWidth - (item.clientWidth * (this.mouseSwipePercentThreshold / 100)) <= dist) {
                        return {
                            width: result.width + item.clientWidth,
                            number: result.number + 1
                        };
                    }

                    return result;
                }, { width: 0, number: 0 })
                : Array.from(this.scrollContainer.children).slice(0, this.state.currentSlide).reverse().reduce((result, item) => {
                    // eslint-disable-next-line max-len
                    if (result.width + item.clientWidth - (item.clientWidth * ((100 - this.mouseSwipePercentThreshold) / 100)) <= Math.abs(dist)) {
                        return {
                            width: result.width + item.clientWidth,
                            number: result.number + 1
                        };
                    }

                    return { ...result, width: result.width + item.clientWidth };
                }, { width: 0, number: 0 });

            this.scrollContainer.style.transform = `translateX(-${this.getTranslateValue(transformX + dist)}px)`;

            if (
                Math.abs(percentMove) > this.mouseSwipePercentThreshold / this.itemsPerScrollContainer
                || items.number > 0
            ) {
                this.shouldSwipe = true;
                this.swipeElementsCount = items.number;
            }
        } else {
            const translateValue = this.state.currentSlide * this.percentageScrollWidth + percentMove;

            const offset = this.itemsOffset * this.percentageScrollWidth;

            this.scrollContainer.style.transform = `translateX(${(translateValue + offset) * -1}%)`;

            if (Math.abs(percentMove) > this.mouseSwipePercentThreshold / this.itemsPerScrollContainer) {
                this.shouldSwipe = true;
                this.swipeElementsCount = Math.ceil(Math.abs(percentMove) / this.percentageScrollWidth);
            }
        }
    }

    processSwipe(target) {
        this.dist = target.pageX - this.startX;
        this.elapsedTime = new Date().getTime() - this.startTime;
        this.isMoveActive = false;

        this.scrollContainer.classList.remove(theme.moveActive);

        const isSwipe = (this.elapsedTime <= this.allowedTime && Math.abs(this.dist) >= this.threshold);

        if (isSwipe) {
            this.slideViewport(this.startX < target.pageX);
        } else if (this.shouldSwipe) {
            this.slideBy(this.swipeElementsCount, this.startX < target.pageX);
        } else {
            this.transformContainer();
        }

        if (!this.intervalId && this.props.isAutoSlideEnabled) {
            this.resetTimer();
        }

        this.scrollContainer.classList.remove(theme.noTransition);
    }

    renderElement = (child, isVirtual, key) => {
        const { shouldSlideByStaticWidth } = this.props;

        if (!child) {
            return;
        }

        return React.cloneElement(child, {
            className: classnames(
                child.props.className,
                theme.carouselItem,
                {
                    [theme.fullWidth]: !shouldSlideByStaticWidth,
                    [theme.virtual]: isVirtual
                }
            ),
            style: getElementStyle(this.elementWidth),
            key,
            onClick: this.elementClickEvent
        });
    }

    renderElementsBefore = () => {
        const { children, isInfinite } = this.props;
        const childrenIndexes = times(this.itemsOffset, Number).reverse();

        if (!children || !isArray(children) || (isArray(children) && !children.length) || !isInfinite) {
            return null;
        }

        return childrenIndexes.map((index) => {
            const childrenIndex = Math.abs((Children.count(children) - index - 1)) % Children.count(children);

            return this.renderElement(Children.toArray(children)[childrenIndex], true, `before-${index}`);
        });
    }

    renderElementsAfter = () => {
        const { children, isInfinite } = this.props;

        if (!children || !isArray(children) || (isArray(children) && !Children.count(children)) || !isInfinite) {
            return null;
        }

        return times(this.itemsOffset, Number).map((index) => {
            const childrenIndex = (index) % Children.count(children);

            return this.renderElement(Children.toArray(children)[childrenIndex], true, `after-${index}`);
        });
    }

    render() {
        const {
            children,
            areIndicatorsEnabled,
            CustomIndicatorElement,
            IndicatorComponent,
            shouldShowIndicatorDot,
            intl,
            noItemsTranslationKey,
            isNextButtonEnabled,
            isPrevButtonEnabled,
            CarouselIndicatorsComponent,
            role
        } = this.props;
        const { isInitialized, currentSlide, indicatorsCount } = this.state;

        const areChildrenDefined = ((!isArray(children) && children) || (isArray(children) && children.length));

        const shouldShowIndicators = Boolean(isInitialized
            && areIndicatorsEnabled
            && areChildrenDefined);

        const shouldShowPrevButton = Boolean(isInitialized
            && isPrevButtonEnabled
            && areChildrenDefined
            && indicatorsCount > 1
            && this.isPrevSlideAvailable);

        const shouldShowNextButton = Boolean(isInitialized
            && isNextButtonEnabled
            && areChildrenDefined
            && indicatorsCount > 1
            && this.isNextSlideAvailable);

        const shouldShowNoItemsMessage = !areChildrenDefined && isInitialized;

        const Indicators = CarouselIndicatorsComponent || CarouselIndicators;

        return (
            <div className={theme.carouselContainer} ref={(ref) => { this.carousel = ref; }}>
                {shouldShowPrevButton && (
                    <div
                      className={theme.previous}
                      onClick={() => this.slideNext(true)}
                      role="button"
                      tabIndex="0"
                    >
                        <IconButton value="ArrowRight" />
                    </div>
                )}
                <div
                  className={classnames(
                    theme.carousel,
                    {
                         [theme.notInitalized]: !isInitialized
                    }
                  )}
                >
                    {!shouldShowNoItemsMessage && (
                        <div className={theme.itemsContainer}>
                            {!isInitialized && <div className={theme.loaderContainer}><Loader /></div>}
                            <div // eslint-disable-line jsx-a11y/no-static-element-interactions
                              className={theme.scrollContainer}
                              ref={(ref) => { this.scrollContainer = ref; }}
                              onTouchStart={this.handleTouchStart}
                              onTouchEnd={this.handleTouchEnd}
                              onTouchMove={this.handleTouchMove}
                              onMouseDown={this.handleMouseDown}
                              onMouseUp={this.handleMouseUp}
                              onMouseMove={this.handleMouseMove}
                              onMouseLeave={this.handleMouseLeave}
                              role={role}
                            >
                                {this.renderElementsBefore()}
                                {Children.map(children, (child, index) => this.renderElement(child, false, index))}
                                {this.renderElementsAfter()}
                            </div>
                        </div>
                    )}
                    {(shouldShowNoItemsMessage && noItemsTranslationKey) && (
                        <div className={theme.noItems}>
                            {intl.formatMessage({ id: noItemsTranslationKey })}
                        </div>
                    )}
                    {shouldShowIndicators && (
                        <Indicators
                          currentSlide={currentSlide}
                          CustomIndicatorElement={CustomIndicatorElement}
                          IndicatorComponent={IndicatorComponent}
                          itemsPerScrollContainer={this.itemsPerScrollContainer}
                          indicatorsCount={indicatorsCount}
                          onIndicatorClick={this.handleIndicatorClick}
                          shouldShowIndicatorDot={shouldShowIndicatorDot}
                        />
                    )}
                </div>
                {shouldShowNextButton && (
                    <div
                      className={theme.next}
                      onClick={() => this.slideNext(false)}
                      role="button"
                      tabIndex="0"
                    >
                        <IconButton value="ArrowRight" />
                    </div>
                )}
            </div>
        );
    }
}

Carousel.propTypes = {
    intl: PropTypes.object.isRequired,
    children: PropTypes.node,
    IndicatorComponent: PropTypes.func,
    areIndicatorsEnabled: PropTypes.bool,
    shouldShowIndicatorDot: PropTypes.bool,
    slideNextTime: PropTypes.number,
    transitionDuration: PropTypes.number,
    shouldSlideByStaticWidth: PropTypes.bool,
    elementWidth: PropTypes.number,
    slideItemsCount: PropTypes.number,
    isAutoSlideEnabled: PropTypes.bool,
    CustomIndicatorElement: PropTypes.func,
    isInitialized: PropTypes.bool,
    isInfinite: PropTypes.bool,
    noItemsTranslationKey: PropTypes.string,
    isPrevButtonEnabled: PropTypes.bool,
    isNextButtonEnabled: PropTypes.bool,
    CarouselIndicatorsComponent: PropTypes.func,
    currentSlide: PropTypes.number,
    role: PropTypes.string
};

Carousel.defaultProps = {
    areIndicatorsEnabled: true,
    slideNextTime: 6000,
    transitionDuration: 400,
    shouldSlideByStaticWidth: false,
    slideItemsCount: 0,
    isAutoSlideEnabled: false,
    isInitialized: false,
    isInfinite: true,
    noItemsTranslationKey: 'App.noItems',
    CustomIndicatorElement: () => null,
    role: 'presentation'
};

export default injectIntl(Carousel);
